// A small, high-quality set of guides that reinforce the India e-commerce
// profitability topic cluster and link naturally into the calculators.
// No thin AI content at scale — 5 focused, genuinely useful articles.

export const GUIDES = {
  "how-to-calculate-meesho-profit": {
    slug: "how-to-calculate-meesho-profit",
    title: "How to Calculate Meesho Profit Correctly",
    description: "A step-by-step method to work out your real per-order profit on Meesho after commission, shipping, GST and returns — not just price minus cost.",
    updated: "2026-06",
    intro: [
      "Most Meesho sellers calculate profit as selling price minus product cost. That number is almost always wrong, because it ignores commission, logistics, GST and — the big one — returns.",
      "Here is a reliable method to find your true per-order profit, and how to pressure-test it before you scale a product.",
    ],
    sections: [
      { h2: "Step 1 — Start with the price the customer actually pays", paragraphs: ["Use the final price the buyer pays, not your MRP. Discounts you fund reduce the revenue you actually collect."] },
      { h2: "Step 2 — Subtract every real cost", paragraphs: ["List product cost, Meesho commission (varies by category), forward and reverse shipping, packaging, GST on the sale, and any advertising. Missing even one of these inflates your profit."] },
      { h2: "Step 3 — Price in your return rate", paragraphs: ["A returned order earns nothing but still costs reverse shipping and packaging. If 20% of orders come back, that loss must be spread across the 80% that deliver. This single factor decides whether many Meesho products are viable."] },
      { h2: "Step 4 — Check break-even and headroom", paragraphs: ["Know your break-even selling price and how much you could spend on ads before an order stops being profitable. Both are shown automatically in our calculator."] },
    ],
    keyTakeaways: [
      "Price minus cost is not profit — commission, GST, shipping and returns matter.",
      "Returns are usually the deciding factor in Meesho profitability.",
      "Always know your break-even price before running ads.",
    ],
    relatedCalculators: ["meesho-profit-calculator", "product-profit-calculator", "cod-profit-calculator"],
    relatedGuides: ["how-cod-and-rto-affect-profit", "meesho-vs-amazon-seller-fees"],
    faq: [
      { q: "Does Meesho charge a commission?", a: "It depends on your category and price slab and has changed over time. Enter your actual category commission rather than assuming a fixed rate." },
    ],
  },

  "how-cod-and-rto-affect-profit": {
    slug: "how-cod-and-rto-affect-profit",
    title: "How COD and RTO Affect E-commerce Profit",
    description: "Cash-on-Delivery drives volume in India, but RTO quietly destroys margins. Here's exactly how returned-to-origin orders eat your profit and how to model them.",
    updated: "2026-06",
    intro: [
      "COD makes Indian shoppers comfortable buying, so it drives order volume. But every COD order carries the risk of RTO — Return To Origin — where the parcel is refused or undelivered and shipped back to you.",
      "On an RTO order you earn zero revenue but still pay forward and reverse shipping, packaging, and usually the ad spend that acquired the order.",
    ],
    sections: [
      { h2: "Why RTO is so expensive", paragraphs: ["A 25% RTO rate means one in four parcels earns nothing yet costs you real money both ways. Those losses must be recovered from your delivered orders, which lowers your effective margin far more than most sellers expect."] },
      { h2: "The right way to model COD profit", paragraphs: ["Split orders into delivered and RTO. Delivered orders carry normal costs plus the COD collection fee. RTO orders carry reverse logistics, packaging and the wasted ad spend. Total profit is delivered profit minus total RTO loss."] },
      { h2: "What to watch", paragraphs: ["Track RTO by pincode and product. A product with great gross margin can still lose money once RTO is included."] },
    ],
    keyTakeaways: [
      "RTO orders earn nothing but still cost you both-way logistics and ads.",
      "Model delivered and RTO orders separately for an honest number.",
      "High RTO can turn a 'profitable' product into a loss maker.",
    ],
    relatedCalculators: ["cod-profit-calculator", "product-profit-calculator", "meesho-profit-calculator"],
    relatedGuides: ["how-to-reduce-rto-losses", "how-to-calculate-meesho-profit"],
    faq: [
      { q: "What RTO rate is normal?", a: "It varies widely by category, price and pincode mix — often 15–40% for COD fashion. Use your own data." },
    ],
  },

  "how-to-reduce-rto-losses": {
    slug: "how-to-reduce-rto-losses",
    title: "How to Reduce RTO Losses",
    description: "Practical levers to cut Return-To-Origin losses for COD e-commerce in India, from order confirmation to pincode strategy and product pages.",
    updated: "2026-06",
    intro: [
      "You can't eliminate RTO, but a few points of improvement flow straight to your bottom line. Use the COD/RTO calculator to see how much each point of RTO is worth to you first.",
    ],
    sections: [
      { h2: "Confirm risky orders", paragraphs: ["Add a quick confirmation (IVR, WhatsApp or SMS) for high-value or first-time COD orders. A large share of RTO comes from impulse or duplicate orders that a confirmation step filters out."] },
      { h2: "Manage risky pincodes", paragraphs: ["Some pincodes consistently return more. Restrict COD or require prepaid on the worst performers, and nudge prepaid with a small discount."] },
      { h2: "Fix expectations on the product page", paragraphs: ["Clear sizing, accurate photos and honest delivery timelines reduce refusals at the door. Mismatched expectations are a top RTO driver."] },
      { h2: "Encourage prepaid", paragraphs: ["Every order you convert from COD to prepaid removes RTO risk entirely. Even a modest prepaid incentive often pays for itself."] },
    ],
    keyTakeaways: [
      "Confirming risky orders removes a big chunk of avoidable RTO.",
      "Pincode-level rules protect margin without killing volume.",
      "Shifting orders to prepaid eliminates RTO risk on those orders.",
    ],
    relatedCalculators: ["cod-profit-calculator", "product-profit-calculator"],
    relatedGuides: ["how-cod-and-rto-affect-profit"],
    faq: [
      { q: "Will reducing COD hurt my sales?", a: "It can reduce volume slightly, but the calculator often shows that lower RTO more than compensates on profit. Test with your own numbers." },
    ],
  },

  "meesho-vs-amazon-seller-fees": {
    slug: "meesho-vs-amazon-seller-fees",
    title: "Meesho vs Amazon Seller Fees: What Actually Matters",
    description: "A clear comparison of how Meesho and Amazon India fees are structured, and why net payout — not the headline commission — decides your profit.",
    updated: "2026-06",
    intro: [
      "Sellers often pick a marketplace based on the headline commission. That's the wrong lens. What matters is your net payout and net profit after every fee, shipping and returns on that specific platform.",
    ],
    sections: [
      { h2: "Different fee structures", paragraphs: ["Amazon India typically charges a referral fee (a % of price), a fixed closing fee, and a weight-handling/shipping fee. Meesho's structure differs and varies by category. Neither is universally cheaper — it depends on your price point and category."] },
      { h2: "Compare on net payout, not commission", paragraphs: ["Two platforms with the same commission can leave you with very different take-home amounts once fixed fees, shipping and GST are included. Always compare the final profit."] },
      { h2: "Use the same numbers on both", paragraphs: ["Run your exact product cost and price through each platform's fees. Our compare tool does this side by side so you can see true profit and margin per platform."] },
    ],
    keyTakeaways: [
      "Headline commission is misleading — compare net payout.",
      "Fee structures differ; the cheaper platform depends on your product.",
      "Always model the same product on each marketplace before deciding.",
    ],
    relatedCalculators: ["marketplace-fee-calculator", "amazon-profit-calculator", "meesho-profit-calculator"],
    relatedGuides: ["how-to-calculate-meesho-profit", "real-profit-on-a-product"],
    faq: [
      { q: "Which marketplace has the lowest fees?", a: "It depends entirely on your category and price point. Compare each platform's net payout for your specific product." },
    ],
  },

  "real-profit-on-a-product": {
    slug: "real-profit-on-a-product",
    title: "How to Calculate the Real Profit on a Product",
    description: "The complete method for finding true product profit — including the hidden costs sellers forget — plus margin, ROI and break-even.",
    updated: "2026-06",
    intro: [
      "Real profit is what's left after every cost of selling one unit, not just the difference between cost and price. Here's the full method, in order.",
    ],
    sections: [
      { h2: "List all the costs", paragraphs: ["Product cost, shipping, packaging, marketplace/platform fees, payment processing, advertising, discounts, returns and tax. If a cost exists, it belongs in the calculation."] },
      { h2: "Compute net profit and margin", paragraphs: ["Net profit is revenue minus all costs. Margin is net profit as a percentage of the selling price. A healthy margin varies by category, but negative or single-digit margins are a warning sign."] },
      { h2: "Check ROI and break-even", paragraphs: ["ROI tells you how hard your money is working. Break-even price tells you the floor below which you lose money. Both should inform your pricing and ad budgets."] },
      { h2: "Stress-test with scenarios", paragraphs: ["See what happens if you raise or lower price, or if returns or fees increase. This is where you find pricing headroom and risk."] },
    ],
    keyTakeaways: [
      "Include every selling cost, not just product cost.",
      "Track net profit, margin, ROI and break-even together.",
      "Use scenarios to find pricing headroom before you commit.",
    ],
    relatedCalculators: ["product-profit-calculator", "marketplace-fee-calculator", "cod-profit-calculator"],
    relatedGuides: ["meesho-vs-amazon-seller-fees", "how-to-calculate-meesho-profit"],
    faq: [
      { q: "What is a good profit margin?", a: "It varies by category, but you want a margin that comfortably covers returns and ad spend with room left over. Use the calculator to test yours." },
    ],
  },
};

export const GUIDE_LIST = Object.values(GUIDES);
